import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import { sortPortfolioDataPoints } from '@products/portfolio/portfolio.utils';
import {
  PortfolioComponentId,
  PortfolioDTO,
  PortfolioHedgeAllocationDTO,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { Mapper, MapperParams } from '../type.mapper';
import { Logger } from '@utils/logger';
import { getFirstNonEmptyProp, parseStdNumber } from '../mapper.utils';

const logger = Logger.getLogger('HedgeAllocationMapper');

export const isPortfolioExposure = (
  point: PortfolioHedgeAllocationDTO
): boolean => point.elemtypestd === 'EXPOSURES';

export const isStrategy = (point: PortfolioHedgeAllocationDTO): boolean =>
  point.elemtypestd === 'STRATEGY';

// tslint:disable-next-line:max-line-length
// example fund: https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/18120/A/franklin-k-2-alternative-strategies-fund/FAAAX#portfolio
export const mapPortfolioExposure = (
  data: PortfolioHedgeAllocationDTO[]
): PortfolioAllocation => ({
  componentId: PortfolioComponentId.PORTFOLIO_EXPOSURE,
  baseLabelKey: 'portfolio-exposure',
  // allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'), // TODO: allocheadinglocal doesn't exist in gql
  asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
  // calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'), // TODO: calcbasislocal doesn't exist in gql
  // calculationType: getFirstNonEmptyProp(data, 'calctypelocal'), // TODO: calctypelocal doesn't exist in gql
  updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
  data: data.sort(sortPortfolioDataPoints).map(
    (obj: PortfolioHedgeAllocationDTO): PortfolioAllocationDataPoint => ({
      label: obj.strtgyallocbrkdwnname,
      breakdown: formatPercentage(obj.strtgyallocbrkdwnpct),
      breakdownStd: parseStdNumber(obj.strtgyallocbrkdwnpct),
    })
  ),
});

// tslint:disable-next-line:max-line-length
// example fund: https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/18120/A/franklin-k-2-alternative-strategies-fund/FAAAX#portfolio
export const mapStrategy = (
  data: PortfolioHedgeAllocationDTO[]
): PortfolioAllocation => ({
  componentId: PortfolioComponentId.STRATEGY_BREAKDOWN,
  baseLabelKey: 'strategy-breakdown',
  asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
  updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
  data: data.sort(sortPortfolioDataPoints).map(
    (obj: PortfolioHedgeAllocationDTO): PortfolioAllocationDataPoint => ({
      label: obj.strtgyallocbrkdwnname,
      targetAllocation: formatPercentage(obj.tgtallocbrkdwnrange),
      targetAllocationStd: parseStdNumber(obj.tgtallocbrkdwnrange),
      actualAllocation: formatPercentage(obj.strtgyallocbrkdwnpct),
      actualAllocationStd: parseStdNumber(obj.strtgyallocbrkdwnpct),
    })
  ),
});

export class HedgeAllocationMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioHedgeAllocationDTO[] = portfolioDto.hedgeallocation;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const allocations: PortfolioAllocation[] = [];

    const portfolioExposureData: PortfolioHedgeAllocationDTO[] = data.filter(
      isPortfolioExposure
    );
    if (portfolioExposureData.length > 0) {
      allocations.push(mapPortfolioExposure(portfolioExposureData));
    }

    const strategyData: PortfolioHedgeAllocationDTO[] = data.filter(isStrategy);
    if (strategyData.length > 0) {
      allocations.push(mapStrategy(strategyData));
    }

    return allocations;
  }
}
